import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Grid,
    Typography,
    Alert,
    CircularProgress,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import axiosInstance from '../../../../../../API/axios';

const SupportRequestDialog = ({ 
    open, 
    onClose, 
    exitPlanId, 
    supportRequest = null, 
    onSaved,
    regions = []
}) => {
    const isEditMode = !!supportRequest;
    
    const [formData, setFormData] = useState({
        providerId: null, // Selected existing provider ID
        providerUsername: '',
        providerEmail: '',
        regionId: null,
        jobTitle: '',
        supportQuery: ''
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [supportProviders, setSupportProviders] = useState([]);
    const [loadingProviders, setLoadingProviders] = useState(false);
    const [isNewProvider, setIsNewProvider] = useState(false);
    const [emailCheckLoading, setEmailCheckLoading] = useState(false);
    const [emailExists, setEmailExists] = useState(null);
    const [existingUserInfo, setExistingUserInfo] = useState(null); // Store info about existing user with different role

    // Fetch existing support providers when dialog opens
    useEffect(() => {
        if (open) {
            fetchSupportProviders();
        }
    }, [open]);

    const fetchSupportProviders = async () => {
        setLoadingProviders(true);
        try {
            const response = await axiosInstance.get('/users/role?roleName=SUPPORT_PROVIDER');
            setSupportProviders(response.data || []);
        } catch (err) {
            console.error('Error fetching support providers:', err);
            setSupportProviders([]);
        } finally {
            setLoadingProviders(false);
        }
    };

    // Reset form when opening dialog
    useEffect(() => {
        if (open) {
            if (supportRequest) {
                // Edit mode - find if provider exists in list
                const existingProvider = supportProviders.find(
                    p => p.email === supportRequest.providerEmail
                );
                setFormData({
                    providerId: existingProvider?.id || null,
                    providerUsername: supportRequest.providerUsername || '',
                    providerEmail: supportRequest.providerEmail || '',
                    regionId: supportRequest.region?.id || null,
                    jobTitle: supportRequest.jobTitle || '',
                    supportQuery: supportRequest.supportQuery || ''
                });
                setIsNewProvider(!existingProvider);
            } else {
                setFormData({
                    providerId: null,
                    providerUsername: '',
                    providerEmail: '',
                    regionId: null,
                    jobTitle: '',
                    supportQuery: ''
                });
                setIsNewProvider(false);
            }
            setError(null);
            setEmailExists(null);
        }
    }, [open, supportRequest, supportProviders]);

    // Check if email exists when typing new email
    useEffect(() => {
        if (!isNewProvider || !formData.providerEmail) {
            setEmailExists(null);
            setExistingUserInfo(null);
            return;
        }

        const checkEmail = async () => {
            if (formData.providerEmail && formData.providerEmail.includes('@')) {
                setEmailCheckLoading(true);
                try {
                    const response = await axiosInstance.get(`/users/check-email?email=${encodeURIComponent(formData.providerEmail)}`);
                    setEmailExists(response.data.exists);
                    
                    if (response.data.exists) {
                        // Store info about the existing user
                        setExistingUserInfo({
                            username: response.data.username,
                            role: response.data.role,
                            isSupportProvider: response.data.isSupportProvider,
                            regionId: response.data.regionId || null,
                            regionName: response.data.regionName || null
                        });
                        
                        // Auto-fill fields from existing user data
                        if (response.data.isSupportProvider) {
                            setFormData(prev => ({
                                ...prev,
                                providerUsername: response.data.username || prev.providerUsername,
                                regionId: response.data.regionId || prev.regionId
                            }));
                        }
                    } else {
                        setExistingUserInfo(null);
                    }
                } catch (err) {
                    setEmailExists(false);
                    setExistingUserInfo(null);
                } finally {
                    setEmailCheckLoading(false);
                }
            }
        };

        const debounce = setTimeout(checkEmail, 500);
        return () => clearTimeout(debounce);
    }, [formData.providerEmail, isNewProvider]);

    const handleProviderSelect = (event) => {
        const selectedId = event.target.value;
        
        if (selectedId === 'new') {
            // User wants to create a new provider
            setIsNewProvider(true);
            setFormData(prev => ({
                ...prev,
                providerId: null,
                providerUsername: '',
                providerEmail: '',
                regionId: null,
                jobTitle: ''
            }));
        } else if (selectedId) {
            // User selected an existing provider
            const provider = supportProviders.find(p => p.id === selectedId);
            if (provider) {
                // Provider may have regions as array or regionId as legacy field
                const providerRegionId = provider.regionId 
                    || (provider.regions && provider.regions.length > 0 ? provider.regions[0].id : null);
                setIsNewProvider(false);
                setFormData(prev => ({
                    ...prev,
                    providerId: provider.id,
                    providerUsername: provider.username || '',
                    providerEmail: provider.email || '',
                    regionId: providerRegionId,
                    jobTitle: provider.jobTitle || ''
                }));
            }
        } else {
            setIsNewProvider(false);
            setFormData(prev => ({
                ...prev,
                providerId: null,
                providerUsername: '',
                providerEmail: '',
                regionId: null,
                jobTitle: ''
            }));
        }
    };

    const handleChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleRegionChange = (event) => {
        setFormData(prev => ({
            ...prev,
            regionId: event.target.value || null
        }));
    };

    const validateForm = () => {
        if (!formData.providerUsername.trim()) {
            setError('Provider username is required');
            return false;
        }
        if (!formData.providerEmail.trim()) {
            setError('Provider email is required');
            return false;
        }
        if (!formData.providerEmail.includes('@')) {
            setError('Please enter a valid email address');
            return false;
        }
        if (!formData.supportQuery.trim()) {
            setError('Support query is required');
            return false;
        }
        // Check if email exists but belongs to non-support-provider
        if (isNewProvider && emailExists && existingUserInfo && !existingUserInfo.isSupportProvider) {
            setError(`This email belongs to an existing ${existingUserInfo.role || 'user'} account. Please select a Support Provider from the dropdown or enter a different email.`);
            return false;
        }
        // If creating new provider, require region
        if (isNewProvider && !emailExists && !formData.regionId) {
            setError('Region is required for new support provider accounts');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError(null);

        try {
            const payload = {
                providerUsername: formData.providerUsername.trim(),
                providerEmail: formData.providerEmail.trim(),
                regionId: formData.regionId,
                jobTitle: formData.jobTitle?.trim() || null,
                supportQuery: formData.supportQuery.trim()
            };

            if (isEditMode) {
                await axiosInstance.put(`/support-requests/${supportRequest.id}`, payload);
            } else {
                await axiosInstance.post('/support-requests', {
                    ...payload,
                    exitPlanId: exitPlanId
                });
            }

            onSaved();
            onClose();
        } catch (err) {
            console.error('Error saving support request:', err);
            setError(err.response?.data?.message || 'Failed to save support request');
        } finally {
            setLoading(false);
        }
    };

    const showNewProviderWarning = isNewProvider && !emailExists && formData.providerEmail && formData.providerEmail.includes('@');
    const showExistingNonSupportProviderError = isNewProvider && emailExists && existingUserInfo;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    {isEditMode ? <EditIcon /> : <AddIcon />}
                    <Typography variant="h6">
                        {isEditMode ? 'Edit Support Request' : 'Create Support Request'}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Support Provider Selection */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Select an existing support provider or create a new one</InputLabel>
                            <Select
                                value={isNewProvider ? 'new' : (formData.providerId || '')}
                                onChange={handleProviderSelect}
                                label="Select an existing support provider or create a new one"
                                disabled={loadingProviders}
                                // placeholder="Select an existing support provider or create a new one"
                            >
                                {/* <MenuItem value="">
                                    <em>-- Select a support provider --</em>
                                </MenuItem> */}
                                {supportProviders.map((provider) => (
                                    <MenuItem key={provider.id} value={provider.id}>
                                        {provider.username} ({provider.email})
                                    </MenuItem>
                                ))}
                                <Divider />
                                <MenuItem value="new">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <PersonAddIcon fontSize="small" color="primary" />
                                        <Typography color="primary">Create New Support Provider</Typography>
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>
                        {loadingProviders && (
                            <Box display="flex" alignItems="center" gap={1} mt={1}>
                                <CircularProgress size={16} />
                                <Typography variant="caption" color="textSecondary">
                                    Loading support providers...
                                </Typography>
                            </Box>
                        )}
                    </Grid>

                    {/* New Provider Warning */}
                    {showNewProviderWarning && (
                        <Grid item xs={12}>
                            <Alert severity="warning" icon={<PersonAddIcon />}>
                                <strong>New Account Will Be Created</strong>
                                <br />
                                No user found with this email. A new Support Provider account will be created 
                                automatically. The user will receive login instructions via email.
                            </Alert>
                        </Grid>
                    )}

                    {/* Error: Email exists but belongs to different role */}
                    {showExistingNonSupportProviderError && (
                        <Grid item xs={12}>
                            <Alert severity="error">
                                <strong>Cannot Use This Email</strong>
                                <br />
                                This email ({formData.providerEmail}) belongs to an existing account 
                                ({existingUserInfo?.username}). You can only assign support requests to Support Provider accounts.
                                <br /><br />
                                Please either:
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                    <li>Select an existing Support Provider from the dropdown above</li>
                                    <li>Enter a different email address to create a new Support Provider account</li>
                                </ul>
                            </Alert>
                        </Grid>
                    )}

                    {/* Provider Details Section */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Provider Username *"
                            value={formData.providerUsername}
                            onChange={handleChange('providerUsername')}
                            fullWidth
                            placeholder="Enter the support provider's name"
                            helperText="The name of the person who will provide support"
                            disabled={!isNewProvider && formData.providerId}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Provider Email *"
                            type="email"
                            value={formData.providerEmail}
                            onChange={handleChange('providerEmail')}
                            fullWidth
                            placeholder="email@unilever.com"
                            helperText={
                                emailCheckLoading 
                                    ? "Checking email..." 
                                    : isNewProvider
                                        ? "Enter email for the new support provider"
                                        : "Email address of the support provider"
                            }
                            disabled={!isNewProvider && formData.providerId}
                            InputProps={{
                                endAdornment: emailCheckLoading ? (
                                    <CircularProgress size={20} />
                                ) : null
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>{isNewProvider && !emailExists ? 'Region *' : 'Region'}</InputLabel>
                            <Select
                                value={formData.regionId || ''}
                                onChange={handleRegionChange}
                                label={isNewProvider && !emailExists ? 'Region *' : 'Region'}
                                disabled={!isNewProvider && formData.providerId}
                            >
                                {/* <MenuItem value="">
                                    <em>-- Select region --</em>
                                </MenuItem> */}
                                {regions.map((region) => (
                                    <MenuItem key={region.id} value={region.id}>
                                        {region.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Job Title"
                            value={formData.jobTitle}
                            onChange={handleChange('jobTitle')}
                            fullWidth
                            placeholder="e.g., Senior Supply Chain Analyst"
                            helperText="Optional: Job title of the support provider"
                            disabled={!isNewProvider && formData.providerId}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Support Query *"
                            value={formData.supportQuery}
                            onChange={handleChange('supportQuery')}
                            fullWidth
                            multiline
                            rows={5}
                            placeholder="Describe what kind of support you need from this provider. Be specific about requirements, timeline expectations, and any relevant context..."
                            helperText="Provide a detailed description of the support needed"
                        />
                    </Grid>
                </Grid>

                {supportRequest?.supportResponse && (
                    <Box mt={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Current Response
                        </Typography>
                        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {supportRequest.supportResponse}
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                            Responded on: {new Date(supportRequest.responseDate).toLocaleString()}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || (!formData.providerId && !isNewProvider) || showExistingNonSupportProviderError}
                    startIcon={loading && <CircularProgress size={20} />}
                >
                    {loading 
                        ? 'Saving...' 
                        : isEditMode 
                            ? 'Update Request' 
                            : 'Create Request'
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SupportRequestDialog;
